import { RacerEntry } from "./RaceOrderManager";
import { CommentaryDatabase } from "./CommentaryDatabase";
import { ISubscribableEvent } from "./ISubscribableEvent";
import { CommentaryEvents } from "./Consts";
import { RacerDatabase } from "./RacerDatabase";

// A class for generating commentary
export class Commentary implements ISubscribableEvent {
  private static instance: Commentary | null = null;
  private latestCommentary: string = "";
  private onCommentaryUpdated: (() => void)[] = [];

  public static getInstance(): Commentary {
    if (Commentary.instance === null) {
      Commentary.instance = new Commentary();
    }
    return Commentary.instance;
  }

  public getLatestCommentary(): string {
    return this.latestCommentary;
  }

  private updateCommentaryMessage(message: string) {
    this.latestCommentary = message;
    this.onCommentaryUpdated.forEach((listener) => listener());
  }

  private getRandomEnding(category: string): string {
    const currentLanguage = window.localStorage.getItem("vs_locale") || "en";
    // const currentLanguage = "fr";

    const options = CommentaryDatabase.getOptions(currentLanguage, category);
    const index = Math.floor(Math.random() * options.length);
    return options[index];
  }

  public processCommentaryStatement(
    previousPositions: RacerEntry[],
    currentPositions: RacerEntry[],
    raceFinished: boolean,
  ) {
    if (raceFinished) {
      this.commentOnRaceFinish(currentPositions[0]);
      return;
    }

    if (this.commentOnNewRaceLeader(previousPositions, currentPositions))
      return;
    if (this.commentOnUnexpectedDrop(previousPositions, currentPositions))
      return;
    if (this.commentOnRacerPositionChange(previousPositions, currentPositions))
      return;

    const remainingOptions = [
      () =>
        this.commentOnMaintainingFirstPlace(
          previousPositions,
          currentPositions,
        ),
      () => this.commentOnMultipleRacers(currentPositions),
      () => this.commentOnRacerInLastPosition(currentPositions),
    ];

    const randomIndex = Math.floor(Math.random() * remainingOptions.length);
    remainingOptions[randomIndex]();
  }

  private commentOnMaintainingFirstPlace(
    previousPositions: RacerEntry[],
    currentPositions: RacerEntry[],
  ) {
    if (previousPositions[0].racerName === currentPositions[0].racerName) {
      this.updateCommentaryMessage(
        `${RacerDatabase.getRacerName(
          currentPositions[0].racerName,
        )} ${this.getRandomEnding("MAINTAINING_FIRST_PLACE")}.`,
      );
    }
  }

  private commentOnNewRaceLeader(
    previousPositions: RacerEntry[],
    currentPositions: RacerEntry[],
  ): boolean {
    if (previousPositions[0].racerName !== currentPositions[0].racerName) {
      this.updateCommentaryMessage(
        `${RacerDatabase.getRacerName(
          currentPositions[0].racerName,
        )} ${this.getRandomEnding("NEW_RACE_LEADER")}.`,
      );
      return true;
    }
    return false;
  }

  private commentOnRacerPositionChange(
    previousPositions: RacerEntry[],
    currentPositions: RacerEntry[],
  ): boolean {
    let mostImproved = { racerName: "", positionsGained: 0 };
    currentPositions.forEach((racer, index) => {
      const prevIndex = previousPositions.findIndex(
        (p) => p.racerName === racer.racerName,
      );
      if (prevIndex !== -1) {
        const positionsGained = prevIndex - index;
        if (positionsGained > mostImproved.positionsGained) {
          mostImproved = { racerName: racer.racerName, positionsGained };
        }
      }
    });
    if (mostImproved.positionsGained > 1) {
      this.updateCommentaryMessage(
        `${RacerDatabase.getRacerName(
          mostImproved.racerName,
        )} ${this.getRandomEnding("MULTIPLE_RACER_POSITION_CHANGE_OPTIONS")}.`,
      );
      return true;
    } else if (mostImproved.positionsGained === 1) {
      this.updateCommentaryMessage(
        ` ${RacerDatabase.getRacerName(
          mostImproved.racerName,
        )} ${this.getRandomEnding("SINGLE_RACER_POSITION_CHANGE_OPTIONS")}.`,
      );
      return true;
    }
    return false;
  }

  private commentOnRacerInLastPosition(currentPositions: RacerEntry[]) {
    this.updateCommentaryMessage(
      ` ${RacerDatabase.getRacerName(
        currentPositions[currentPositions.length - 1].racerName,
      )} ${this.getRandomEnding("RACER_IN_LAST_POSITION_OPTIONS")}.`,
    );
  }

  private commentOnMultipleRacers(currentPositions: RacerEntry[]) {
    const topRacers = [
      currentPositions[0],
      currentPositions[1],
      currentPositions[2],
    ];
    this.updateCommentaryMessage(this.generateMultiRacerCommentary(topRacers));
  }

  private commentOnUnexpectedDrop(
    previousPositions: RacerEntry[],
    currentPositions: RacerEntry[],
  ): boolean {
    let hasCommented = false;
    previousPositions.slice(0, 3).forEach((racer) => {
      const currentIndex = currentPositions.findIndex(
        (p) => p.racerName === racer.racerName,
      );
      if (currentIndex > 3) {
        this.updateCommentaryMessage(
          `${RacerDatabase.getRacerName(
            racer.racerName,
          )} ${this.getRandomEnding("FALLEN_OUT_OF_TOP_THREE_OPTIONS")}.`,
        );
        hasCommented = true;
      }
    });
    return hasCommented;
  }

  private generateMultiRacerCommentary(racers: RacerEntry[]): string {
    const currentLanguage = window.localStorage.getItem("vs_locale") || "en";
    // const currentLanguage = "fr";
    const randomIndex = Math.floor(
      Math.random() *
        CommentaryDatabase.getOptions(
          currentLanguage,
          "MULTI_RACER_COMMENTARY_OPTIONS",
        ).length,
    );
    let phrase = CommentaryDatabase.getOptions(
      currentLanguage,
      "MULTI_RACER_COMMENTARY_OPTIONS",
    )[randomIndex];
    phrase = phrase.replace(
      "{racer1}",
      RacerDatabase.getRacerName(racers[0].racerName.toString()),
    );
    phrase = phrase.replace(
      "{racer2}",
      RacerDatabase.getRacerName(racers[1].racerName.toString()),
    );
    phrase = phrase.replace(
      "{racer3}",
      RacerDatabase.getRacerName(racers[2].racerName.toString()),
    );
    return phrase;
  }

  private commentOnRaceFinish(winningRacer: RacerEntry) {
    this.updateCommentaryMessage(
      `${RacerDatabase.getRacerName(
        winningRacer.racerName,
      )} ${this.getRandomEnding("RACE_FINISHED_OPTIONS")}`,
    );
  }

  public subscribeToEvent(eventName: string, listener: () => void): void {
    if (eventName === CommentaryEvents.ON_COMMENTARY_UPDATED) {
      if (!this.onCommentaryUpdated.includes(listener)) {
        this.onCommentaryUpdated.push(listener);
      } else {
        console.log(
          `Listener already exists in onCommentaryUpdated array for ${listener}`,
        );
      }
    } else {
      throw new Error("Event name not recognized.");
    }
  }

  public unsubscribeFromEvent(eventName: string, listener: () => void): void {
    if (eventName === CommentaryEvents.ON_COMMENTARY_UPDATED) {
      const index = this.onCommentaryUpdated.indexOf(listener);
      if (index !== -1) {
        this.onCommentaryUpdated.splice(index, 1);
      }
    } else {
      throw new Error("Event name not recognized.");
    }
  }
}
